import React, { useState, useEffect, useContext } from 'react';
import { Card, Typography, Tag, Space, Button, message, Divider, Avatar, Modal } from 'antd';
import { LikeOutlined, EyeOutlined, ShareAltOutlined, FieldTimeOutlined, UserOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAllPosts, deletePost } from '../../api/mockData'; // 使用 getAllPosts 而不是 posts，并添加 deletePost
import { AuthContext } from '../../contexts/AuthContext'; // 引入 AuthContext
import './PostDetail.css';

const { Title, Text, Paragraph } = Typography;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext); // 获取当前登录用户
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [post, setPost] = useState(null);
  
  // 根据ID查找文章，使用 getAllPosts 获取所有文章（包括存储在 localStorage 中的）
  useEffect(() => {
    const allPosts = getAllPosts();
    const foundPost = allPosts.find(p => p.id === id);
    setPost(foundPost);
    if (foundPost) {
      setLikeCount(foundPost.likes || 0);
    }
  }, [id]);

  const handleLike = () => {
    if (liked) {
      setLikeCount(prevCount => prevCount - 1);
    } else {
      setLikeCount(prevCount => prevCount + 1);
    }
    setLiked(!liked);
    message.success(liked ? '已取消点赞' : '点赞成功');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      message.success('链接已复制到剪贴板');
    }
  };

  // 检查当前用户是否有权限编辑文章
  const canEdit = user && post && (user.name === 'qmau' || post.authorId === user.id);
  
  // 处理删除文章
  const handleDelete = () => {
    if (canEdit) {
      Modal.confirm({
        title: '确认删除',
        content: '确定要删除这篇文章吗？此操作不可恢复。',
        okText: '确认',
        cancelText: '取消',
        onOk: async () => {
          try {
            await deletePost(id);
            message.success('文章删除成功！');
            navigate('/'); // 删除后跳转到首页
          } catch (error) {
            message.error('删除文章失败');
          }
        }
      });
    } else {
      message.error('您没有权限删除此文章');
    }
  };

  // 编辑文章处理函数
  const handleEdit = () => {
    if (canEdit) {
      navigate(`/write/${id}`);
    } else {
      message.error('您没有权限编辑此文章');
    }
  };

  // 如果找不到文章，显示404信息
  if (!post) {
    return (
      <div className="post-detail-page">
        <Card className="post-detail-card">
          <Title level={2}>文章不存在</Title>
          <Paragraph>抱歉，您访问的文章不存在或已被删除。</Paragraph>
          <Link to="/">返回首页</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      <Card className="post-detail-card">
        <div className="post-header">
          <Title level={1}>{post.title}</Title>
          
          <div className="post-meta">
            <Space size="middle">
              <span>
                <UserOutlined /> {post.author}
              </span>
              <span>
                <FieldTimeOutlined /> {post.date}
              </span>
              <span>
                <EyeOutlined /> {post.views || 0} 浏览
              </span>
            </Space>
            
            {/* 只有当前用户有编辑权限时才显示编辑和删除按钮 */}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {canEdit && (
                <>
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />} 
                    onClick={handleEdit}
                  >
                    编辑文章
                  </Button>
                  <Button 
                    danger
                    icon={<DeleteOutlined />} 
                    onClick={handleDelete}
                  >
                    删除文章
                  </Button>
                </>
              )}
            </div>
            
            <div className="post-tags" style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
              {post.tags && post.tags.map((tag, index) => (
                <Tag key={index} color="blue">{tag}</Tag>
              ))}
            </div>
          </div>
        </div>
        
        <Divider />
        
        <div className="post-content">
          <Paragraph className="post-full-content">
            {post.content}
          </Paragraph>
        </div>
        
        <Divider />
        
        <div className="post-actions">
          <Space size="middle">
            <Button 
              icon={<LikeOutlined />} 
              type={liked ? "primary" : "default"}
              onClick={handleLike}
              danger={liked}
            >
              {liked ? '已点赞' : '点赞'} {likeCount}
            </Button>
            <Button 
              icon={<ShareAltOutlined />} 
              onClick={handleShare}
            >
              分享
            </Button>
          </Space>
        </div>
        
        <div className="post-author-info">
          <Divider />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar size={64} icon={<UserOutlined />} />
            <div>
              <Title level={4}>{post.author}</Title>
              <Text type="secondary">这是一篇关于 {post.tags && post.tags[0] ? post.tags[0] : '文章'} 的文章</Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PostDetail;